import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Clock, Calendar } from 'lucide-react';
import SelectFlatRateModal from './SelectFlatRateModal';
import { FlatRateItem } from '../lib/flat-rates';
import { createPayrollRequest, getExistingFlatRateCompletionTimestamp } from '../lib/payrollRequests';
import toast from 'react-hot-toast';

interface RequestFlatRateEntriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId: string;
  bookingNumber: number;
  bookingTitle: string;
  businessId: string;
  resourceId: string;
  onSuccess: () => void;
}

interface SelectedFlatRateItem {
  item: FlatRateItem;
  hours: number;
}

export default function RequestFlatRateEntriesModal({
  isOpen,
  onClose,
  bookingId,
  bookingNumber,
  bookingTitle,
  businessId,
  resourceId,
  onSuccess
}: RequestFlatRateEntriesModalProps) {
  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<SelectedFlatRateItem[]>([]);
  const [requestReason, setRequestReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingCompletionTimestamp, setExistingCompletionTimestamp] = useState<string | null>(null);

  // Load existing completion timestamp when modal opens
  React.useEffect(() => {
    if (isOpen && bookingId && resourceId) {
      loadExistingCompletionTimestamp();
    }
  }, [isOpen, bookingId, resourceId]);

  const loadExistingCompletionTimestamp = async () => {
    try {
      console.log('🔍 Looking for existing flat rate completion timestamp for:', { bookingId, resourceId });
      const timestamp = await getExistingFlatRateCompletionTimestamp(bookingId, resourceId);
      setExistingCompletionTimestamp(timestamp);
      
      if (timestamp) {
        console.log('✅ Found existing completion timestamp:', timestamp);
        console.log('📅 Will inherit completion time:', new Date(timestamp).toLocaleString());
      } else {
        console.log('❌ No existing completed flat rate items found for this resource on this booking');
        console.log('⏰ New items will use approval timestamp when approved');
      }
    } catch (error) {
      console.error('❗ Error loading existing completion timestamp:', error);
      setExistingCompletionTimestamp(null);
    }
  };

  if (!isOpen) return null;

  const handleAddItems = () => {
    setIsSelectModalOpen(true);
  };

  const handleItemsSelected = async (item: FlatRateItem, hours: number, resourceId: string) => {
    // Add the selected item to our list
    setSelectedItems(prev => [...prev, { item, hours }]);
    setIsSelectModalOpen(false);
  };

  const handleRemoveItem = (itemIndex: number) => {
    setSelectedItems(prev => prev.filter((_, index) => index !== itemIndex));
  };

  const handleSubmit = async () => {
    if (selectedItems.length === 0) {
      setError('Please select at least one flat rate item');
      return;
    }

    if (!requestReason.trim()) {
      setError('Please provide a reason for this request');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Create payroll requests for each selected item
      for (const selectedItem of selectedItems) {
        await createPayrollRequest({
          business_id: businessId,
          resource_id: resourceId,
          request_type: 'add_flat_rate',
          requested_booking_id: bookingId,
          requested_flat_rate_item_id: selectedItem.item.id,
          requested_flat_rate_hours: selectedItem.hours,
          requested_flat_rate_completed_at: existingCompletionTimestamp || undefined,
          request_reason: requestReason
        });
      }

      toast.success(`Flat rate request submitted for Job #${bookingNumber}`);
      onSuccess();
      onClose();
      
      // Reset form
      setSelectedItems([]);
      setRequestReason('');
    } catch (err) {
      console.error('Error submitting flat rate request:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit flat rate request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedItems([]);
    setRequestReason('');
    setError(null);
    setExistingCompletionTimestamp(null);
    onClose();
  };

  const totalHours = selectedItems.reduce((sum, item) => sum + item.hours, 0);

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Request New Flat Rate Entries
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Job #{bookingNumber} - {bookingTitle}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 max-h-[calc(90vh-200px)] overflow-y-auto">
          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <Calendar className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-blue-800">
                  Request Additional Flat Rate Items
                </h3>
                <p className="mt-1 text-sm text-blue-700">
                  Add flat rate items that were performed on this job but not originally included. 
                  Your request will be reviewed by management before being approved.
                </p>
                {existingCompletionTimestamp && (
                  <div className="mt-2 text-xs text-blue-600 bg-blue-100 rounded px-2 py-1">
                    <Clock className="h-3 w-3 inline mr-1" />
                    These items will inherit the completion date/time from your existing work on this job.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Selected Items */}
          {selectedItems.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Selected Items ({selectedItems.length})</h3>
              <div className="space-y-2">
                {selectedItems.map((selectedItem, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium text-sm text-gray-900">{selectedItem.item.name}</div>
                      {selectedItem.item.description && (
                        <div className="text-xs text-gray-600 mt-1">{selectedItem.item.description}</div>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {selectedItem.hours.toFixed(1)} hours
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveItem(index)}
                      className="text-gray-400 hover:text-red-600 p-1"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-3 p-3 bg-purple-50 rounded-lg">
                <div className="text-sm font-medium text-purple-900">
                  Total: {totalHours.toFixed(1)} hours
                </div>
              </div>
            </div>
          )}

          {/* Add Items Button */}
          <div className="mb-6">
            <button
              onClick={handleAddItems}
              className="w-full flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors"
            >
              <Plus className="h-5 w-5 text-gray-400" />
              <span className="text-sm font-medium text-gray-600">
                {selectedItems.length === 0 ? 'Add Flat Rate Items' : 'Add More Items'}
              </span>
            </button>
          </div>

          {/* Reason Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Request *
            </label>
            <textarea
              value={requestReason}
              onChange={(e) => setRequestReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              placeholder="Explain why these additional flat rate items should be added to this job..."
            />
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50">
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || selectedItems.length === 0}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Submitting...
              </>
            ) : (
              'Submit Request'
            )}
          </button>
        </div>
      </div>

      {/* SelectFlatRateModal */}
      <SelectFlatRateModal
        isOpen={isSelectModalOpen}
        onClose={() => setIsSelectModalOpen(false)}
        onAdd={handleItemsSelected}
        selectedResourceId={resourceId}
        bookingId={bookingId}
      />
    </div>
  );

  return createPortal(modalContent, document.body);
}